import { translations, type SupportedLanguage } from "../locales";
import {
  FREE_CAPTIONS_LANDING_PATH as CAPTIONS_LANDING_PATH,
  FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE,
  getCaptionsLandingPath,
} from "../content/captionsLanding";
import {
  CREATORS_LANDING_PATH,
  CREATORS_LANDING_PATHS_BY_LANGUAGE,
  getCreatorsLandingPath,
} from "../content/creatorsLanding";
import {
  AUDIO_TRANSLATION_LANDING_PATH as AUDIO_TRANSLATION_PAGE_PATH,
  AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE,
  getAudioTranslationLandingPath,
  isAudioTranslationPairPath as isAudioTranslationPairBasePath,
} from "../content/audioTranslation";

export const DUBBING_LANDING_PATH = "/ai-video-voice-over-dubbing" as const;
export const FREE_CAPTIONS_LANDING_PATH = CAPTIONS_LANDING_PATH;
export const AUDIO_TRANSLATION_LANDING_PATH = AUDIO_TRANSLATION_PAGE_PATH;
export const DUBBING_PAIR_PATH_PREFIX = `${DUBBING_LANDING_PATH}-english-to-` as const;
export const DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH = `${DUBBING_PAIR_PATH_PREFIX}spanish` as const;
export const SPANISH_DUBBING_LANDING_PATH = "/es/doblaje-video-ia" as const;
export const SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH =
  "/es/traduce-video-doblaje-ia-espanol-ingles" as const;
const LANGUAGE_PREFIX_PATH_RE = /^\/([a-z]{2})(?:\/(.*))?$/i;
const BLOG_ARTICLE_EN_RE = /^\/blog\/([^/]+)$/i;
const BLOG_ARTICLE_LOCALIZED_RE = /^\/([a-z]{2})\/blog\/([^/]+)$/i;
const ENGLISH_ONLY_BASE_PATHS = new Set<string>(["/privacy", "/terms", "/cookie-policy"]);
const NON_TRAILING_SLASH_BASE_PATHS = new Set<string>(["/cookie-policy"]);
const BASE_PATH_ALIASES: Record<string, LocalizedBasePath> = {
  "/cookies": "/cookie-policy",
};

export type LocalizedBasePath = string;

const englishBasePaths: string[] = [
  "/",
  "/blog",
  "/privacy",
  "/terms",
  "/cookie-policy",
  CREATORS_LANDING_PATH,
  DUBBING_LANDING_PATH,
  FREE_CAPTIONS_LANDING_PATH,
  AUDIO_TRANSLATION_LANDING_PATH,
];

const nonRootBasePathSet = new Set<string>(englishBasePaths.filter((path) => path !== "/"));

const spanishSeoAliases: Record<string, LocalizedBasePath> = {
  [SPANISH_DUBBING_LANDING_PATH]: DUBBING_LANDING_PATH,
  [SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH]: DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH,
};

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function toDirectoryRoutePath(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function toLocalizedRoutePath(pathname: string): string {
  const normalizedPath = normalizePathname(pathname);

  if (!normalizedPath || normalizedPath === "/") {
    return "/";
  }

  if (NON_TRAILING_SLASH_BASE_PATHS.has(normalizedPath)) {
    return normalizedPath;
  }

  return toDirectoryRoutePath(normalizedPath);
}

const creatorsLandingBasePathByLocalizedPath = Object.fromEntries(
  Object.entries(CREATORS_LANDING_PATHS_BY_LANGUAGE)
    .filter(([language]) => language !== "en")
    .map(([, path]) => [normalizePathname(path), CREATORS_LANDING_PATH]),
) as Record<string, LocalizedBasePath>;

const creatorsLandingLanguageByLocalizedPath = Object.fromEntries(
  Object.entries(CREATORS_LANDING_PATHS_BY_LANGUAGE).map(([language, path]) => [
    normalizePathname(path),
    language as SupportedLanguage,
  ]),
) as Record<string, SupportedLanguage>;

const captionsLandingBasePathByLocalizedPath = Object.fromEntries(
  Object.entries(FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE)
    .filter(([language]) => language !== "en")
    .map(([, path]) => [normalizePathname(path), FREE_CAPTIONS_LANDING_PATH]),
) as Record<string, LocalizedBasePath>;

const captionsLandingLanguageByLocalizedPath = Object.fromEntries(
  Object.entries(FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE).map(([language, path]) => [
    normalizePathname(path),
    language as SupportedLanguage,
  ]),
) as Record<string, SupportedLanguage>;

const audioTranslationLandingBasePathByLocalizedPath = Object.fromEntries(
  Object.entries(AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE)
    .filter(([language]) => language !== "en")
    .map(([, path]) => [normalizePathname(path), AUDIO_TRANSLATION_LANDING_PATH]),
) as Record<string, LocalizedBasePath>;

const audioTranslationLandingLanguageByLocalizedPath = Object.fromEntries(
  Object.entries(AUDIO_TRANSLATION_LANDING_PATHS_BY_LANGUAGE).map(([language, path]) => [
    normalizePathname(path),
    language as SupportedLanguage,
  ]),
) as Record<string, SupportedLanguage>;

function parseLanguagePrefixedPath(
  pathname: string
): { language: SupportedLanguage; restPath: string } | null {
  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(LANGUAGE_PREFIX_PATH_RE);
  if (!match) {
    return null;
  }

  const [, languageCode, rest] = match;
  const normalizedLanguage = languageCode.toLowerCase();
  if (!isSupportedLanguage(normalizedLanguage)) {
    return null;
  }

  const restPath = rest ? normalizePathname(`/${rest}`) : "/";
  return {
    language: normalizedLanguage,
    restPath,
  };
}

export function getBlogArticleSlugFromUrlPath(pathname: string): string | null {
  const normalizedPathname = normalizePathname(pathname);
  const localizedMatch = normalizedPathname.match(BLOG_ARTICLE_LOCALIZED_RE);

  if (localizedMatch) {
    const [, lang, slug] = localizedMatch;
    if (isSupportedLanguage(lang.toLowerCase())) {
      return decodeURIComponent(slug);
    }
  }

  const englishMatch = normalizedPathname.match(BLOG_ARTICLE_EN_RE);
  if (englishMatch) {
    return decodeURIComponent(englishMatch[1]);
  }

  return null;
}

export function isSupportedLanguage(value?: string | null): value is SupportedLanguage {
  return Boolean(value) && Object.prototype.hasOwnProperty.call(translations, value);
}

export function getLocalizedPath(path: LocalizedBasePath, language: SupportedLanguage): string {
  const normalizedPath = normalizePathname(path);

  if (ENGLISH_ONLY_BASE_PATHS.has(normalizedPath)) {
    return toLocalizedRoutePath(normalizedPath);
  }

  if (normalizedPath === FREE_CAPTIONS_LANDING_PATH) {
    return toLocalizedRoutePath(getCaptionsLandingPath(language));
  }

  if (normalizedPath === CREATORS_LANDING_PATH) {
    return toLocalizedRoutePath(getCreatorsLandingPath(language));
  }

  if (normalizedPath === AUDIO_TRANSLATION_LANDING_PATH) {
    return toLocalizedRoutePath(getAudioTranslationLandingPath(language));
  }

  if (language === "es") {
    if (normalizedPath === DUBBING_LANDING_PATH) {
      return toLocalizedRoutePath(SPANISH_DUBBING_LANDING_PATH);
    }
    if (normalizedPath === DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH) {
      return toLocalizedRoutePath(SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH);
    }
  }

  if (language === "en") {
    return toLocalizedRoutePath(normalizedPath);
  }

  if (normalizedPath === "/") {
    return `/${language}/`;
  }

  return toLocalizedRoutePath(`/${language}${normalizedPath}`);
}

export function isDubbingPairPath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);

  return (
    normalizedPathname.startsWith(DUBBING_PAIR_PATH_PREFIX) &&
    normalizedPathname.length > DUBBING_PAIR_PATH_PREFIX.length
  );
}

export function isAudioTranslationPairPath(pathname: string): boolean {
  return isAudioTranslationPairBasePath(normalizePathname(pathname));
}

export function getBasePathFromUrlPath(pathname: string): LocalizedBasePath | null {
  const normalizedPathname = normalizePathname(pathname);
  const aliasedPathname = BASE_PATH_ALIASES[normalizedPathname] || normalizedPathname;

  if (getBlogArticleSlugFromUrlPath(normalizedPathname)) {
    return "/blog";
  }

  if (creatorsLandingBasePathByLocalizedPath[normalizedPathname]) {
    return creatorsLandingBasePathByLocalizedPath[normalizedPathname];
  }

  if (captionsLandingBasePathByLocalizedPath[normalizedPathname]) {
    return captionsLandingBasePathByLocalizedPath[normalizedPathname];
  }

  if (audioTranslationLandingBasePathByLocalizedPath[normalizedPathname]) {
    return audioTranslationLandingBasePathByLocalizedPath[normalizedPathname];
  }

  if (spanishSeoAliases[normalizedPathname]) {
    return spanishSeoAliases[normalizedPathname];
  }

  if (
    englishBasePaths.includes(aliasedPathname) ||
    isDubbingPairPath(aliasedPathname) ||
    isAudioTranslationPairPath(aliasedPathname)
  ) {
    return aliasedPathname;
  }

  const prefixedPath = parseLanguagePrefixedPath(normalizedPathname);
  if (prefixedPath) {
    const { language, restPath } = prefixedPath;
    const aliasedRestPath = BASE_PATH_ALIASES[restPath] || restPath;

    if (language === "en") {
      if (aliasedRestPath === "/") {
        return "/";
      }
      return nonRootBasePathSet.has(aliasedRestPath) ||
        isDubbingPairPath(aliasedRestPath) ||
        isAudioTranslationPairPath(aliasedRestPath)
        ? aliasedRestPath
        : null;
    }

    if (aliasedRestPath === "/") {
      return "/";
    }
    return nonRootBasePathSet.has(aliasedRestPath) ||
      isDubbingPairPath(aliasedRestPath) ||
      isAudioTranslationPairPath(aliasedRestPath)
      ? aliasedRestPath
      : null;
  }

  return null;
}

export function getLanguageFromUrlPath(pathname: string): SupportedLanguage | null {
  const normalizedPathname = normalizePathname(pathname);

  const basePath = getBasePathFromUrlPath(normalizedPathname);

  if (basePath === "/privacy" || basePath === "/terms" || basePath === "/cookie-policy") {
    return "en";
  }

  const localizedBlogArticleMatch = normalizedPathname.match(BLOG_ARTICLE_LOCALIZED_RE);

  if (localizedBlogArticleMatch) {
    const [, lang] = localizedBlogArticleMatch;
    return isSupportedLanguage(lang.toLowerCase()) ? lang.toLowerCase() : null;
  }

  if (BLOG_ARTICLE_EN_RE.test(normalizedPathname)) {
    return "en";
  }

  if (creatorsLandingLanguageByLocalizedPath[normalizedPathname]) {
    return creatorsLandingLanguageByLocalizedPath[normalizedPathname];
  }

  if (captionsLandingLanguageByLocalizedPath[normalizedPathname]) {
    return captionsLandingLanguageByLocalizedPath[normalizedPathname];
  }

  if (audioTranslationLandingLanguageByLocalizedPath[normalizedPathname]) {
    return audioTranslationLandingLanguageByLocalizedPath[normalizedPathname];
  }

  if (spanishSeoAliases[normalizedPathname]) {
    return "es";
  }

  if (
    englishBasePaths.includes(normalizedPathname) ||
    isDubbingPairPath(normalizedPathname) ||
    isAudioTranslationPairPath(normalizedPathname)
  ) {
    return "en";
  }

  const prefixedPath = parseLanguagePrefixedPath(normalizedPathname);
  if (prefixedPath) {
    const { language, restPath } = prefixedPath;
    if (restPath === "/") {
      return language;
    }
    return nonRootBasePathSet.has(restPath) ||
      isDubbingPairPath(restPath) ||
      isAudioTranslationPairPath(restPath)
      ? language
      : null;
  }

  return null;
}

export function isDubbingLandingPath(pathname: string): boolean {
  return getBasePathFromUrlPath(pathname) === DUBBING_LANDING_PATH;
}

export function isDubbingLandingFamilyPath(pathname: string): boolean {
  const basePath = getBasePathFromUrlPath(pathname);
  return basePath === DUBBING_LANDING_PATH || (basePath ? isDubbingPairPath(basePath) : false);
}

export function isCreatorsLandingPath(pathname: string): boolean {
  return getBasePathFromUrlPath(pathname) === CREATORS_LANDING_PATH;
}
